import api from 'services/api';
import { REJECT, array, bool, nullable, num, numeric, object, oneOf, str, type Received } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { filterConfigSchema, listColumnConfigSchema, type FilterConfig, type ListColumnConfig } from 'api/shared/listConfig';
import { readResponse } from 'api/shared/readResponse';

import {
  TRIGGER_ACTIONS,
  TRIGGER_RESTART_FIRE_MODE_DEFAULT,
  TRIGGER_RESTART_FIRE_MODES,
  triggerParameterSchema,
  type TriggerAction,
  type TriggerParameter,
  type TriggerRestartFireMode,
} from './types';

// GET /triggers — the list read. Backend: routes/triggers.js `GET /triggers` →
// `getTriggers` (modules/generic_functions/getTriggers.js) for the paginated rows,
// then the route attaches `folder_path` per row (buildFolderPath) and wraps them as
// `{ triggers, pagination, config, filtersConfig }`.
//
// THE SHAPE THAT MATTERS HERE: this row is FLAT. The list query LEFT JOINs
// `triggers_node_status_change` and selects its columns inline, whereas the
// single-GET / POST / PUT handlers nest the same columns under `status_change`.
// So `TriggerRow` is not merely a separate declaration of `Trigger` — it is a
// different shape, and no code should reach for `row.status_change`.
//
// Everything from the joined table is therefore NULLABLE HERE EVEN WHERE THE COLUMN
// IS `NOT NULL` (`action`, `restart_fire_mode`): a trigger with no status-change row
// still produces a row, with those fields null. `zone_name` is nullable for two
// reasons — the join is LEFT, and `triggers.zone_id` itself has no `NOT NULL`
// (migration 0180).
export interface TriggerRow {
  readonly trigger_id: number;
  readonly zone_id: number | null;
  readonly folder_id: number | null;
  readonly zone_name: string | null;
  readonly name: string;
  readonly type: string;
  readonly monitor_all_zones: boolean;
  // Always arrays: the monitor-zone LATERAL joins `ON TRUE` and COALESCEs its
  // aggregates, so a trigger monitoring nothing explicit yields `[]`, not null.
  // Empty + `monitor_all_zones` is how "all zones, present and future" is encoded.
  readonly monitor_zone_ids: readonly number[];
  readonly monitor_zone_names: readonly string[];
  // Nullable because the columns are: migration 0180 declares both
  // `TIMESTAMP DEFAULT CURRENT_TIMESTAMP` with no `NOT NULL`, and nothing in the
  // route guarantees a value either. A default fills them on every INSERT the
  // application makes, so a null needs direct SQL to arise — but `str()` would
  // report that null as an empty string, which is a worse answer than "unknown".
  readonly created_at: string | null;
  readonly updated_at: string | null;
  // --- flattened from triggers_node_status_change (all null when absent) ---
  readonly trigger_node_status_change_id: number | null;
  readonly node_definition_id: number | null;
  readonly node_definition_name: string | null;
  readonly from_status: string | null;
  readonly to_status: string | null;
  readonly action: TriggerAction | null;
  readonly target_node_definition_id: number | null;
  readonly target_node_definition_name: string | null;
  readonly target_event_id: number | null;
  readonly target_event_name: string | null;
  readonly target_event_zone_name: string | null;
  readonly queue_id: number | null;
  readonly queue_name: string | null;
  readonly queue_zone_name: string | null;
  readonly parameters: readonly TriggerParameter[] | null;
  readonly restart_fire_mode: TriggerRestartFireMode | null;
  readonly timezone: string | null;
  // Enriched per-row by the list route (null when the trigger has no folder).
  readonly folder_path: FolderPath | null;
}

interface Pagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

export interface TriggersPage {
  readonly triggers: readonly TriggerRow[];
  readonly pagination: Pagination;
  // List-table config, shared across list endpoints (see api/shared/listConfig).
  readonly config: readonly ListColumnConfig[];
  readonly filtersConfig: readonly FilterConfig[];
}

// `type` stays a plain string rather than `oneOf(TRIGGER_TYPES)`: the column is a
// free VARCHAR with no CHECK, the list's own filter dropdown is built from
// `SELECT DISTINCT type`, and a future type must show up in the list rather than
// being silently rewritten to `node_status_change`. `action` and
// `restart_fire_mode` are genuinely closed (validated on every write; the latter
// also by a DB constraint), so those do narrow.
//
// The literal passed to each `oneOf` is the parser's required floor, not the value
// a drifted field actually takes: `nullable` overrides an inner default with `null`
// (validation/combinators.ts), which is the honest answer here — an unrecognised
// mode reads as "unknown" rather than being fabricated as a real one.
const rowSchema = object<TriggerRow>({
  trigger_id: num(REJECT),
  zone_id: nullable(num()),
  folder_id: nullable(num()),
  zone_name: nullable(str()),
  name: str(),
  type: str(),
  monitor_all_zones: bool(),
  monitor_zone_ids: array(num()),
  monitor_zone_names: array(str()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  trigger_node_status_change_id: nullable(num()),
  node_definition_id: nullable(num()),
  node_definition_name: nullable(str()),
  from_status: nullable(str()),
  to_status: nullable(str()),
  action: nullable(oneOf(TRIGGER_ACTIONS, 'raise_event')),
  target_node_definition_id: nullable(num()),
  target_node_definition_name: nullable(str()),
  target_event_id: nullable(num()),
  target_event_name: nullable(str()),
  target_event_zone_name: nullable(str()),
  queue_id: nullable(num()),
  queue_name: nullable(str()),
  queue_zone_name: nullable(str()),
  parameters: nullable(array(triggerParameterSchema)),
  restart_fire_mode: nullable(oneOf(TRIGGER_RESTART_FIRE_MODES, TRIGGER_RESTART_FIRE_MODE_DEFAULT)),
  timezone: nullable(str()),
  folder_path: nullable(folderPathSchema),
});

// COUNT-derived totals can arrive as numeric strings from Postgres.
const paginationSchema = object<Pagination>({
  total_records: numeric(REJECT),
  total_pages: numeric(REJECT),
  current_page: numeric(),
  limit: numeric(),
  offset: numeric(),
});

const pageSchema = object<TriggersPage>({
  triggers: array(rowSchema),
  pagination: paginationSchema,
  config: array(listColumnConfigSchema),
  filtersConfig: array(filterConfigSchema),
});

/**
 * GET `/triggers`. Returns the full list page (rows + pagination + list config).
 *
 * A row whose `trigger_id` drifted is dropped — every per-row action addresses the
 * trigger by that id, so a row we cannot identify must not reach the table. A
 * drifted pagination total makes the whole page unreadable instead, since a list
 * cannot be paged from a number it cannot trust. Rejects on transport error.
 *
 * `action` and `restart_fire_mode` carry no `REJECT`: they are display columns
 * here, and defaulting an unrecognised value is a better list than no list.
 */
export const getTriggers = async (params: Record<string, unknown> = {}): Promise<Received<TriggersPage>> => {
  const res = await api.get('/triggers', { params });
  return readResponse(res.data, { schema: pageSchema, source: 'getTriggers' });
};
